import type { TimeRange } from '@/infra/time-range'

/**
 * Stages of a consultation as it relates to displaying to the user.
 */
export type ConsultationDisplayStage =
  | ConsultationAwaitingPickupDisplayStage
  | ConsultationAwaitingProConfirmationDisplayStage
  | ConsultationBookedDisplayStage
  | ConsultationDayOfCallDisplayStage
  | ConsultationInProgressDisplayStage
  | ConsultationCallCompletedDisplayStage
  | ConsultationRescheduleNeededDisplayStage

interface ConsultationRescheduleNeededDisplayStage {
  type: 'RescheduleNeeded'
}
/**
 * The consultation is awaiting pickup.
 */
interface ConsultationAwaitingPickupDisplayStage {
  type: 'AwaitingPickup'
  availability: TimeRange[]
  requestedLength: number
}

interface ConsultationAwaitingProConfirmationDisplayStage {
  type: 'AwaitingProConfirmation'
  availability: TimeRange[]
}

/**
 * The consultation is booked.
 */
interface ConsultationBookedDisplayStage {
  type: 'Booked'
  bookedTime: TimeRange
  requestedLength: number
  providerDisplayName: string
}

/**
 * The consultation is on the day of call.
 */
interface ConsultationDayOfCallDisplayStage {
  type: 'DayOfCall'
  bookedTime: TimeRange
  requestedLength: number
  providerDisplayName: string
}

/**
 * The transcript is available for download.
 */
interface ConsultationCallCompletedDisplayStage {
  type: 'CallCompleted'
  bookedTime: TimeRange
  requestedLength: number
  providerDisplayName: string
}

interface ConsultationInProgressDisplayStage {
  type: 'InProgress'
}

/**
 * Match arms used for {@link matchConsultationDisplayStage}.
 */
interface MatchConsultationDisplayArmsCore<R> {
  awaitingPickup: (stage: ConsultationAwaitingPickupDisplayStage) => R
  awaitingProConfirmation: (
    stage: ConsultationAwaitingProConfirmationDisplayStage,
  ) => R
  booked: (stage: ConsultationBookedDisplayStage) => R
  dayOfCall: (stage: ConsultationDayOfCallDisplayStage) => R
  inProgress: (stage: ConsultationInProgressDisplayStage) => R
  callCompleted: (stage: ConsultationCallCompletedDisplayStage) => R
  rescheduleNeeded: () => R
}

/**
 * Match arms used for {@link matchConsultationDisplayStage}.
 */
export type MatchConsultationDisplayArms<R> =
  | MatchConsultationDisplayArmsCore<R>
  | (Partial<MatchConsultationDisplayArmsCore<R>> & { default: () => R })

/**
 * Matches on the consultation display stage.
 *
 * @param stage
 * @param arms
 */
export function matchConsultationDisplayStage<T>(
  stage: ConsultationDisplayStage,
  arms: MatchConsultationDisplayArms<T>,
): T {
  if (stage.type === 'AwaitingPickup' && arms.awaitingPickup) {
    return arms.awaitingPickup(stage)
  }

  if (
    stage.type === 'AwaitingProConfirmation' &&
    arms.awaitingProConfirmation
  ) {
    return arms.awaitingProConfirmation(stage)
  }

  if (stage.type === 'Booked' && arms.booked) {
    return arms.booked(stage)
  }

  if (stage.type === 'DayOfCall' && arms.dayOfCall) {
    return arms.dayOfCall(stage)
  }

  if (stage.type === 'CallCompleted' && arms.callCompleted) {
    return arms.callCompleted(stage)
  }

  if (stage.type === 'InProgress' && arms.inProgress) {
    return arms.inProgress(stage)
  }

  if (stage.type === 'RescheduleNeeded' && arms.rescheduleNeeded) {
    return arms.rescheduleNeeded()
  }

  if ('default' in arms) {
    return arms.default()
  }

  // Should be unreachable.
  throw new Error('Unhandled consultation display stage')
}

export interface ConsultationStepDescriptor {
  title: string
  description: string
  stage?: ConsultationDisplayStage['type']
}

/**
 * Expectation steps for consultations.
 */
export const consultationExpectationsSteps: ConsultationStepDescriptor[] = [
  {
    stage: 'AwaitingPickup',
    title: 'Awaiting Pro pickup',
    description:
      'We will connect you to a Pro who will best assist with your inquiry. While you wait, you can adjust your availability.',
  },
  {
    stage: 'Booked',
    title: 'Consultation booked',
    description:
      'Once a Pro connects, they will book the consultation and notify you of the selected date and time. If you need to reschedule, you can do so up until 24 hours before the booked time slot.',
  },
  {
    stage: 'DayOfCall',
    title: 'The day of the call',
    description:
      "On the day of the consultation, we'll send you reminders to log in and join the call. If you downloaded the calendar event, you will be able to connect from there, too.",
  },
  {
    stage: 'InProgress',
    title: 'Meet with your Pro',
    description: 'Arrange the best time to meet in chat.',
  },
  {
    stage: 'CallCompleted',
    title: 'Your transcript, generated',
    description:
      'We will generate a transcript of your consultation after the call with your Pro. This will take a few minutes, but it will always be available for you to download.',
  },
  {
    title: 'Rate your experience',
    description:
      'Your feedback is important to us and helps us to give our customers a better experience.',
  },
]
